import React, { useCallback, useMemo, useState } from "react";
import { Route, RouteComponentProps } from "react-router-dom";
import {
  useIsMobile,
  useModalContext,
  WSElement,
  WSFilters,
  WSPage,
  WSText
} from "@wingspanhq/fe-component-library";

import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import DropdownButton from "../../../components/DropdownButton";
import { Tabs } from "../../../components/Tabs/Tabs";
import {
  AddCollaboratorModal,
  ADD_COLLABORATOR_MODAL
} from "../../../Invoices/components/AddCollaboratorModal/AddCollaboratorModal";
import { useCollaboratorsQuery } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { getCurrentFilingYear } from "../../utils";

import styles from "./styles.module.scss";
import { NEC1099FilingTable } from "../NEC1099FilingTable";
import NEC1099FilingDetails from "../NEC1099FilingDetails";
import getCollaborators, { DashboardScope } from "./getCollaborators";
import { Tooltip } from "../../../components/Tooltip";
import { getSortedCollaborators } from "../../../query/payments/selectors";

export { DashboardScope };

// TODO(@Anesh): add number badge later.
const tabs = [
  {
    title: "All",
    path: `/member/1099-filing/dashboard/${DashboardScope.All}`
  },
  {
    title: "Needs action",
    path: `/member/1099-filing/dashboard/${DashboardScope.NeedsAction}`
  },
  {
    title: "Ready",
    path: `/member/1099-filing/dashboard/${DashboardScope.Ready}`
  },
  {
    title: "Submitted",
    path: `/member/1099-filing/dashboard/${DashboardScope.Submitted}`
  },
  {
    title: "Excluded",
    path: `/member/1099-filing/dashboard/${DashboardScope.Exlduded}`
  }
];

export const NEC1099FilingDashboard: React.FC<RouteComponentProps<{
  scope: DashboardScope;
}>> = ({ match, history }) => {
  const currentFilingYear = getCurrentFilingYear();
  useBrowserPageTitle(`1099-NEC Filing Dashboard ${currentFilingYear}`);
  const [searchText, setSearchText] = useState("");

  const isMobile = useIsMobile();
  const { openModal } = useModalContext();
  const collaboratorsQuery = useCollaboratorsQuery();

  const dashboardScope = match.params.scope;

  const onClose = useCallback(() => history.push(match.url), [
    history,
    match.url
  ]);

  const menuItems = useMemo(
    () => [
      {
        label: "Share all for review",
        onClick() {
          alert("Share all for review: Coming Soon!");
        }
      },
      {
        label: "Submit all for filing",
        onClick() {
          alert("Submit all for filing: Coming Soon!");
        }
      },
      {
        label: "Import payments",
        onClick() {
          alert("Import payments: Coming Soon!");
        }
      },
      {
        label: "Add collaborator",
        onClick() {
          openModal(ADD_COLLABORATOR_MODAL);
        }
      },
      {
        icon: "download" as const,
        label: "Download as CSV",
        onClick() {
          alert("Download as CSV: Coming Soon!");
        }
      }
    ],
    [openModal]
  );

  const headerContent = useMemo(
    () => (
      <>
        <WSText.Heading1 mr="S" inline>
          1099-NEC Filing Dashboard
        </WSText.Heading1>
        <WSText.Label className={styles.filingYear} inline color="grey600">
          {currentFilingYear}
        </WSText.Label>
      </>
    ),
    [currentFilingYear]
  );

  const sideContent = useMemo(
    () => (
      <Tooltip
        showInfoIcon={false}
        trigger={
          <DropdownButton
            name="actions"
            text="Share all for review"
            disabled
            onClick={() => {}}
            menuItems={menuItems}
            ml="S"
            mb="XS"
            testId="shareAllForReviewButton"
          />
        }
      >
        This feature is currently unavailable. It will be enabled in Jan 2023.
      </Tooltip>
    ),
    [menuItems]
  );

  return (
    <WSQueries queries={{ collaboratorsQuery }}>
      {({ collaboratorsQuery: { data: allCollaborators } }) => {
        // filter out the clients who paid themselves
        const filteredCollaborators = allCollaborators.filter(
          collaborator => collaborator.memberId !== collaborator.clientId
        );
        const collaborators = getSortedCollaborators(
          getCollaborators(searchText, dashboardScope, filteredCollaborators)
        );

        return (
          <>
            <Route
              path={`${match.path}/:collaboratorId`}
              render={routeProps => (
                <NEC1099FilingDetails onClose={onClose} {...routeProps} />
              )}
            />

            <WSPage headerContent={headerContent} sideContent={sideContent}>
              <AddCollaboratorModal />

              <Tabs links={tabs} />

              <WSFilters
                onSearch={newSearchText => {
                  setSearchText(newSearchText.toLowerCase());
                }}
                searchQuery={searchText}
                my={isMobile ? "S" : "L"}
              />

              <WSElement mt="L" data-testid="nec1099FilingContainer">
                <NEC1099FilingTable collaborators={collaborators} />
              </WSElement>
            </WSPage>
          </>
        );
      }}
    </WSQueries>
  );
};
